
import { PanelDashboardModel, PanelDashboardSortTypeEnum, PanelDashboardLayoutTypeEnum } from '@/libs/Api';
import Page from '@/Page.vue';
import { Guid } from '@/libs/common/functions/guid';
import { Component, Prop, Ref, Watch } from 'vue-property-decorator';
import NotificationScopeSelect from '@/libs/common/components-smart/NotificationScopeSelect.vue';
import { NotificationScope } from '@/libs/common/model/NotificationScope';
import { panelDashboardsStore } from '@/libs/panel-dashboards/+state/store';
import { CrudAction, CrudReponse } from '@/libs/core/+state/models/crud-action';
import { CrudGetter } from '@/libs/core/+state/models/crud-getter';

@Component({
    components: { NotificationScopeSelect },
})
export default class PanelDashboardDetailDialog extends Page {
    dialog = false;
    valid = true;
    loading = true;
    saving = false;
    deleteDialog = false;
    cloneAudit = false;
    item: PanelDashboardModel | null = null;
    locationScope: NotificationScope | null = null;
    @Ref() notificationScopeSelect!: NotificationScopeSelect;

    @Watch('dialog')
    dialogChanged(v: boolean) {
        if (!v) this.close();
    }

    get isNew(): boolean {
        return this.item?.id == Guid.EmptyGuid();
    }

    get isDeleting(): boolean {
        return panelDashboardsStore.useGetter(CrudGetter.Deleting);
    }

    get sortOptions() {
        return [
            {
                value: PanelDashboardSortTypeEnum.ByWorkplace,
                text: this.$i18n.t('panelDashboards.sortOptions.byWorkplace'),
            },
            {
                value: PanelDashboardSortTypeEnum.ByReportTime,
                text: this.$i18n.t('panelDashboards.sortOptions.byReportTime'),
            },
            {
                value: PanelDashboardSortTypeEnum.ByShutdownDuration,
                text: this.$i18n.t('panelDashboards.sortOptions.byShutdownDuration'),
            },
            {
                value: PanelDashboardSortTypeEnum.ByFaultPriority,
                text: this.$i18n.t('panelDashboards.sortOptions.byFaultPriority'),
            },
        ];
    }

    get layoutOptions() {
        return [
            {
                value: PanelDashboardLayoutTypeEnum.OnlyTable,
                text: this.$i18n.t('panelDashboards.layoutOptions.onlyTable'),
            },
            {
                value: PanelDashboardLayoutTypeEnum.TableWithOee,
                text: this.$i18n.t('panelDashboards.layoutOptions.tableWithCharts'),
            },
            {
                value: PanelDashboardLayoutTypeEnum.OnlyOee,
                text: this.$i18n.t('panelDashboards.layoutOptions.onlyCharts'),
            },
        ];
    }

    get hasTable(): boolean {
        return (
            this.item?.layoutType === PanelDashboardLayoutTypeEnum.OnlyTable ||
            this.item?.layoutType === PanelDashboardLayoutTypeEnum.TableWithOee
        );
    }

    get hasCharts(): boolean {
        return (
            this.item?.layoutType === PanelDashboardLayoutTypeEnum.OnlyOee ||
            this.item?.layoutType === PanelDashboardLayoutTypeEnum.TableWithOee
        );
    }

    get showOeeOptions(): boolean {
        // Zobrazit OEE komponenty pokud:
        // jsou grafy (které vždy zobrazují OEE)
        return this.hasCharts;
    }

    get hasAtLeastOneOeeComponent(): boolean {
        return (
            this.item?.showOverallOee === true ||
            this.item?.showOeeAvailability === true ||
            this.item?.showOeePerformance === true ||
            this.item?.showOeeQuality === true
        );
    }

    get isOeeValidationError(): boolean {
        // Validační chyba pokud jsou grafy a není zaškrtnuta žádná OEE složka
        return this.hasCharts && !this.hasAtLeastOneOeeComponent;
    }

    @Ref() form!: any;
    @Prop() tenantId!: string;

    async load(id: string | null = null) {
        if (id) {
            panelDashboardsStore.dispatch(CrudAction.Get, { id });
            this.item = await this.subscribe(panelDashboardsStore, CrudReponse.Get);
            this.locationScope = this.item?.locationScope ? JSON.parse(this.item?.locationScope) : null;
            // Nastavení výchozích hodnot pro nové vlastnosti
            if (this.item) {
                if (!this.item.layoutType) {
                    this.item.layoutType = PanelDashboardLayoutTypeEnum.OnlyTable;
                }
                if (this.item.showOeeInTable === undefined || this.item.showOeeInTable === null) {
                    this.item.showOeeInTable = false;
                }
                if (this.item.showOverallOee === undefined || this.item.showOverallOee === null) {
                    this.item.showOverallOee = true;
                }
                if (this.item.showOeeAvailability === undefined || this.item.showOeeAvailability === null) {
                    this.item.showOeeAvailability = true;
                }
                if (this.item.showOeePerformance === undefined || this.item.showOeePerformance === null) {
                    this.item.showOeePerformance = true;
                }
                if (this.item.showOeeQuality === undefined || this.item.showOeeQuality === null) {
                    this.item.showOeeQuality = true;
                }
            }
            this.loading = false;
        } else {
            this.item = {
                id: Guid.EmptyGuid(),
                name: '',
                locationScope: null,
                isActive: true,
                language: this.$i18n.locale,
                sortType: PanelDashboardSortTypeEnum.ByWorkplace,
                sortAscending: true,
                layoutType: PanelDashboardLayoutTypeEnum.OnlyTable,
                showOeeInTable: false,
                showOverallOee: true,
                showOeeAvailability: true,
                showOeePerformance: true,
                showOeeQuality: true,
            } as PanelDashboardModel;
            this.loading = false;
        }
    }

    async open(id: string | null = null) {
        this.loading = true;
        this.dialog = true;
        this.cloneAudit = false;
        await this.load(id);
    }

    close() {
        this.dialog = false;
    }

    save() {
        if (this.form.validate()) {
            this.saving = true;
            const item = { ...this.item };
            item.locationScope = JSON.stringify(this.notificationScopeSelect.getScope());
            if (item.id == Guid.EmptyGuid()) {
                panelDashboardsStore.dispatch(CrudAction.Create, { item });
                this.subscribe(panelDashboardsStore, CrudReponse.Create)
                    .then((e: PanelDashboardModel) => {
                        this.saving = false;
                        this.close();
                    })
                    .catch((e: any) => {
                        this.saving = false;
                    });
            } else {
                panelDashboardsStore.dispatch(CrudAction.Update, { item });
                this.subscribe(panelDashboardsStore, CrudReponse.Update)
                    .then((e: PanelDashboardModel) => {
                        this.saving = false;
                        this.close();
                    })
                    .catch((e: any) => {
                        this.saving = false;
                    });
            }
        }
    }

    deleteClick() {
        this.deleteDialog = true;
    }

    deleteItemConfirm() {
        panelDashboardsStore.dispatch(CrudAction.Delete, { id: this.item!.id! });
        this.subscribe(panelDashboardsStore, CrudReponse.Delete).then((e: PanelDashboardModel) => {
            this.deleteDialog = false;
            this.close();
        });
    }
}
